/**
 * NoemaGraphDiffView — Side-by-Side Graph Diff Visualization
 *
 * Compares two intentional graphs: green=added objects, red=removed objects,
 * yellow=changed stance. Matching lines connect corresponding objects.
 *
 * @module @humanizer/studio/components/tools/NoemaGraphDiffView
 */

import React, { useMemo, useState } from 'react';
import type {
  NoemaGraphResult, NoemaObject, NoemaMetrics,
  NoeticActCategory,
} from '../../types/analysis';
import type { NoemaGraphDiffResult, NoemaStanceShift } from '../../types/analysis';
import '../../styles/noema-graph.css';

// -----------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------

const CATEGORY_COLORS: Record<NoeticActCategory, string> = {
  constitutive: 'var(--noema-constitutive)',
  temporal: 'var(--noema-temporal)',
  evaluative: 'var(--noema-evaluative)',
  epistemic: 'var(--noema-epistemic)',
  relational: 'var(--noema-relational)',
};

const OBJECT_TYPE_COLORS: Record<string, string> = {
  person: 'var(--noema-obj-person)',
  place: 'var(--noema-obj-place)',
  event: 'var(--noema-obj-event)',
  concept: 'var(--noema-obj-concept)',
  sensation: 'var(--noema-obj-sensation)',
  artifact: 'var(--noema-obj-artifact)',
  relation: 'var(--noema-obj-relation)',
  state: 'var(--noema-obj-state)',
};

// -----------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------

export interface NoemaGraphDiffViewProps {
  sourceGraph: NoemaGraphResult;
  targetGraph: NoemaGraphResult;
  diff: NoemaGraphDiffResult;
  sourceLabel?: string;
  targetLabel?: string;
  className?: string;
}

interface ObjectPosition {
  id: string;
  label: string;
  y: number;
  salience: number;
  objectType: string;
  stance: string;
}

// -----------------------------------------------------------------------
// Summary Panel
// -----------------------------------------------------------------------

function DiffSummary({ diff }: { diff: NoemaGraphDiffResult }) {
  const driftPct = (diff.overallDrift * 100).toFixed(1);
  const tensionPct = (diff.tensionPreserved * 100).toFixed(0);
  const noveltyPct = (diff.noveltyPreserved * 100).toFixed(0);

  return (
    <div className="noema-diff__summary">
      <div className="noema-diff__summary-item">
        <span className="noema-diff__summary-label">Overall Drift</span>
        <span className="noema-diff__summary-value noema-diff__summary-value--drift">
          {driftPct}%
        </span>
      </div>
      <div className="noema-diff__summary-item">
        <span className="noema-diff__summary-label">Tension Preserved</span>
        <span className="noema-diff__summary-value">{tensionPct}%</span>
      </div>
      <div className="noema-diff__summary-item">
        <span className="noema-diff__summary-label">Novelty Preserved</span>
        <span className="noema-diff__summary-value">{noveltyPct}%</span>
      </div>
      <div className="noema-diff__summary-item">
        <span className="noema-diff__summary-label">Objects Lost</span>
        <span className="noema-diff__summary-value noema-diff__summary-value--lost">
          {diff.objectsLost.length}
        </span>
      </div>
      <div className="noema-diff__summary-item">
        <span className="noema-diff__summary-label">Objects Added</span>
        <span className="noema-diff__summary-value noema-diff__summary-value--added">
          {diff.objectsAdded.length}
        </span>
      </div>
      <div className="noema-diff__summary-item">
        <span className="noema-diff__summary-label">Stance Shifts</span>
        <span className="noema-diff__summary-value noema-diff__summary-value--shifted">
          {diff.stanceShifts.length}
        </span>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------
// Object List (with diff coloring)
// -----------------------------------------------------------------------

function DiffObjectList({
  objects,
  lostIds,
  addedIds,
  shiftedLabels,
  side,
}: {
  objects: NoemaObject[];
  lostIds: Set<string>;
  addedIds: Set<string>;
  shiftedLabels: Set<string>;
  side: 'source' | 'target';
}) {
  const sorted = useMemo(
    () => [...objects].sort((a, b) => b.salience - a.salience),
    [objects]
  );

  return (
    <div className="noema-diff__object-list">
      {sorted.map(obj => {
        let statusClass = '';
        if (side === 'source' && lostIds.has(obj.id)) {
          statusClass = 'noema-diff__object--lost';
        } else if (side === 'target' && addedIds.has(obj.id)) {
          statusClass = 'noema-diff__object--added';
        } else if (shiftedLabels.has(obj.label)) {
          statusClass = 'noema-diff__object--shifted';
        }

        return (
          <div key={obj.id} className={`noema-diff__object ${statusClass}`}>
            <span
              className="noema-objects__dot"
              style={{ backgroundColor: OBJECT_TYPE_COLORS[obj.objectType] || 'var(--noema-obj-concept)' }}
            />
            <span className="noema-diff__object-label">{obj.label}</span>
            <span className="noema-diff__object-meta">
              {(obj.salience * 100).toFixed(0)}% | {obj.stance}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// -----------------------------------------------------------------------
// Metrics Comparison
// -----------------------------------------------------------------------

function MetricsComparison({
  sourceMetrics,
  targetMetrics,
  sourceLabel,
  targetLabel,
}: {
  sourceMetrics: NoemaMetrics;
  targetMetrics: NoemaMetrics;
  sourceLabel: string;
  targetLabel: string;
}) {
  const items: Array<{ label: string; source: number; target: number }> = [
    { label: 'Tension', source: sourceMetrics.tension, target: targetMetrics.tension },
    { label: 'Novelty', source: sourceMetrics.novelty, target: targetMetrics.novelty },
    { label: 'Surprisal', source: sourceMetrics.surprisal, target: targetMetrics.surprisal },
    { label: 'Depth', source: sourceMetrics.engagementDepth, target: targetMetrics.engagementDepth },
  ];

  return (
    <div className="noema-diff__metrics-comparison">
      <div className="noema-diff__metrics-header">
        <span />
        <span className="noema-diff__metrics-label">{sourceLabel}</span>
        <span className="noema-diff__metrics-label">{targetLabel}</span>
        <span className="noema-diff__metrics-label">Delta</span>
      </div>
      {items.map(item => {
        const delta = item.target - item.source;
        const deltaClass = delta > 0.05 ? 'noema-diff__delta--positive'
          : delta < -0.05 ? 'noema-diff__delta--negative'
          : '';
        return (
          <div key={item.label} className="noema-diff__metrics-row">
            <span className="noema-diff__metrics-name">{item.label}</span>
            <span className="noema-diff__metrics-value">{(item.source * 100).toFixed(0)}%</span>
            <span className="noema-diff__metrics-value">{(item.target * 100).toFixed(0)}%</span>
            <span className={`noema-diff__metrics-value ${deltaClass}`}>
              {delta > 0 ? '+' : ''}{(delta * 100).toFixed(0)}%
            </span>
          </div>
        );
      })}
    </div>
  );
}

// -----------------------------------------------------------------------
// Stance Shifts
// -----------------------------------------------------------------------

function StanceShiftList({ shifts }: { shifts: NoemaStanceShift[] }) {
  if (shifts.length === 0) return null;

  return (
    <div className="noema-diff__shifts">
      <div className="noema-diff__shifts-header">
        Stance Shifts ({shifts.length})
      </div>
      {shifts.map((shift, i) => (
        <div key={i} className="noema-diff__shift">
          <span className="noema-diff__shift-label">{shift.label}</span>
          <span className="noema-diff__shift-arrow">
            <span className="noema-diff__shift-stance">{shift.fromStance}</span>
            {' \u2192 '}
            <span className="noema-diff__shift-stance">{shift.toStance}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

// -----------------------------------------------------------------------
// Main Component
// -----------------------------------------------------------------------

export function NoemaGraphDiffView({
  sourceGraph,
  targetGraph,
  diff,
  sourceLabel = 'Source',
  targetLabel = 'Target',
  className = '',
}: NoemaGraphDiffViewProps): React.ReactElement {
  const [viewMode, setViewMode] = useState<'objects' | 'metrics' | 'shifts'>('objects');

  // Build lookup sets for coloring
  const lostIds = useMemo(
    () => new Set(diff.objectsLost.map(o => o.id)),
    [diff.objectsLost]
  );
  const addedIds = useMemo(
    () => new Set(diff.objectsAdded.map(o => o.id)),
    [diff.objectsAdded]
  );
  const shiftedLabels = useMemo(
    () => new Set(diff.stanceShifts.map(s => s.label)),
    [diff.stanceShifts]
  );

  return (
    <div className={`noema-diff ${className}`}>
      {/* Summary bar */}
      <DiffSummary diff={diff} />

      {/* View mode tabs */}
      <div className="noema-graph__tabs">
        <button
          className={`noema-graph__tab ${viewMode === 'objects' ? 'noema-graph__tab--active' : ''}`}
          onClick={() => setViewMode('objects')}
        >
          Objects
        </button>
        <button
          className={`noema-graph__tab ${viewMode === 'metrics' ? 'noema-graph__tab--active' : ''}`}
          onClick={() => setViewMode('metrics')}
        >
          Metrics
        </button>
        <button
          className={`noema-graph__tab ${viewMode === 'shifts' ? 'noema-graph__tab--active' : ''}`}
          onClick={() => setViewMode('shifts')}
        >
          Shifts ({diff.stanceShifts.length})
        </button>
      </div>

      {/* Objects: side-by-side */}
      {viewMode === 'objects' && (
        <div className="noema-diff__side-by-side">
          <div className="noema-diff__column">
            <div className="noema-diff__column-header">{sourceLabel}</div>
            <DiffObjectList
              objects={sourceGraph.objects}
              lostIds={lostIds}
              addedIds={addedIds}
              shiftedLabels={shiftedLabels}
              side="source"
            />
          </div>
          <div className="noema-diff__column">
            <div className="noema-diff__column-header">{targetLabel}</div>
            <DiffObjectList
              objects={targetGraph.objects}
              lostIds={lostIds}
              addedIds={addedIds}
              shiftedLabels={shiftedLabels}
              side="target"
            />
          </div>
        </div>
      )}

      {/* Metrics comparison */}
      {viewMode === 'metrics' && (
        <MetricsComparison
          sourceMetrics={sourceGraph.metrics}
          targetMetrics={targetGraph.metrics}
          sourceLabel={sourceLabel}
          targetLabel={targetLabel}
        />
      )}

      {/* Stance shifts */}
      {viewMode === 'shifts' && (
        <StanceShiftList shifts={diff.stanceShifts} />
      )}

      {/* Legend */}
      <div className="noema-diff__legend">
        <span className="noema-diff__legend-item noema-diff__legend-item--lost">
          Lost
        </span>
        <span className="noema-diff__legend-item noema-diff__legend-item--added">
          Added
        </span>
        <span className="noema-diff__legend-item noema-diff__legend-item--shifted">
          Stance Changed
        </span>
        <span className="noema-diff__legend-item">
          Preserved
        </span>
      </div>

      {/* Processing info */}
      <div className="noema-graph__meta">
        {sourceLabel}: {sourceGraph.objects.length} objects, {sourceGraph.acts.length} acts |
        {' '}{targetLabel}: {targetGraph.objects.length} objects, {targetGraph.acts.length} acts
      </div>
    </div>
  );
}

export default NoemaGraphDiffView;
